import { r, s } from '@/theme/tokens';
import { Box, ChevronRightIcon, ExpandIcon, Text } from '@/ui/primitives';
import { landingCopy } from './landingCopy';
import { landingSpacing } from './landingSpacing';
import { LANDING_BODY_FONT_SIZE } from './landingTypography';
import type { ReactElement } from 'react';

type FlowStepsSectionProps = {
  width: number;
};

const underlineColors = ['primary', 'purple', 'pink'] as const;
const stepIcons = ['/logo.png', '/logo-purple.png', '/logo-pink.png'] as const;

const steps = landingCopy.flowSteps;

export const FlowStepsSection = ({ width }: FlowStepsSectionProps): ReactElement => {
  const gridItems = steps.flatMap((step, index) => {
    const items = [
      <Box
        key={step.title}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: r('card'),
          paddingY: s('2xl'),
          paddingX: s('xl'),
          paddingTop: s('xl'),
          backgroundColor: 'background.paper',
          height: '100%',
        }}
      >
        <Text role="sectionTitle" component="h3" transform="none" mb="md">
          <Box
            component="span"
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: s('sm'),
              paddingBottom: s('md'),
              borderBottom: `${s('xs')} solid ${
                theme.app.colors[underlineColors[index % underlineColors.length]]
              }`,
            })}
          >
            <Box
              component="img"
              src={stepIcons[index % stepIcons.length]}
              aria-hidden
              sx={(theme) => ({
                width: theme.app.iconSizes.lg,
                height: theme.app.iconSizes.lg,
                display: 'block',
                flexShrink: 0,
                transform: 'translateY(3px)',
                imageRendering: 'auto',
              })}
            />
            <Box component="span">{step.title}</Box>
          </Box>
        </Text>
        <Box component="ul" sx={{ m: s('none'), pl: s('lg'), color: 'text.secondary' }}>
          {step.bullets.map((bullet) => (
            <li key={bullet}>
              <Text role="bodyMuted" component="span" sx={{ fontSize: LANDING_BODY_FONT_SIZE }}>
                {bullet}
              </Text>
            </li>
          ))}
        </Box>
      </Box>,
    ];

    if (index < steps.length - 1) {
      items.push(
        <Box
          key={`${step.title}-chevron`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            px: s('sm'),
            py: { xs: s('sm'), md: s('none') },
          }}
        >
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <ExpandIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <ChevronRightIcon sx={{ fontSize: 36 }} />
          </Box>
        </Box>,
      );
    }

    return items;
  });

  return (
    <Box
      component="section"
      sx={{
        px: { xs: s('3xl'), sm: s('4xl'), md: s('none') },
        mt: landingSpacing.sections.flowStepsTopOffset,
      }}
    >
      <Box sx={{ maxWidth: width, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr)',
            },
            rowGap: s('lg'),
            columnGap: s('lg'),
            alignItems: 'stretch',
          }}
        >
          {gridItems}
        </Box>
      </Box>
    </Box>
  );
};
